import { isApiErrorResponse, type ApiErrorBody, type ApiErrorCode } from '@portfolio/shared';

export const apiBaseUrl = (import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3001/api/v1')
  .trim()
  .replace(/\/+$/, '');

/** Requests are abandoned rather than left hanging if the API is unreachable. */
const REQUEST_TIMEOUT_MS = 12_000;

/**
 * A failed API call, normalised so the UI never has to branch on `fetch`
 * mechanics. `code` is always one of the shared API error codes.
 */
export class ApiRequestError extends Error {
  readonly code: ApiErrorCode;
  readonly status: number;
  readonly issues: readonly { field: string; message: string }[];
  readonly requestId: string | undefined;

  constructor(body: ApiErrorBody, status: number) {
    super(body.message);
    this.name = 'ApiRequestError';
    this.code = body.code;
    this.status = status;
    this.issues = body.issues ?? [];
    this.requestId = body.requestId;
  }
}

const NETWORK_ERROR: ApiErrorBody = {
  code: 'INTERNAL_ERROR',
  message:
    'The message could not be sent because the server could not be reached. Please try again, or email directly.',
};

async function readJson(response: Response): Promise<unknown> {
  try {
    return (await response.json()) as unknown;
  } catch {
    return null;
  }
}

export async function apiPost<TResponse>(path: string, body: unknown): Promise<TResponse> {
  let response: Response;

  try {
    response = await fetch(`${apiBaseUrl}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
      signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
    });
  } catch {
    // Network failure, DNS failure, CORS rejection or timeout. None of these
    // carry anything useful to show a visitor.
    throw new ApiRequestError(NETWORK_ERROR, 0);
  }

  const payload = await readJson(response);

  if (!response.ok) {
    if (isApiErrorResponse(payload)) {
      throw new ApiRequestError(payload.error, response.status);
    }
    throw new ApiRequestError(
      {
        code: response.status === 429 ? 'RATE_LIMITED' : 'INTERNAL_ERROR',
        message:
          response.status === 429
            ? 'Too many messages have been sent from this network. Please try again in a minute.'
            : 'Something went wrong while sending your message. Please try again shortly.',
      },
      response.status,
    );
  }

  return payload as TResponse;
}

export async function apiGet<TResponse>(path: string): Promise<TResponse> {
  try {
    const response = await fetch(`${apiBaseUrl}${path}`, {
      headers: { Accept: 'application/json' },
      signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
    });

    if (!response.ok) {
      throw new ApiRequestError(NETWORK_ERROR, response.status);
    }

    return (await response.json()) as TResponse;
  } catch (error) {
    if (error instanceof ApiRequestError) {
      throw error;
    }
    throw new ApiRequestError(NETWORK_ERROR, 0);
  }
}
